import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { formatMoney } from '@kopi-kaki/protocol';
import type { Direction, Look } from '@kopi-kaki/protocol';
import { avatarKey, drawAvatar } from '../game/avatarArt.js';
import type { AvatarAction } from '../game/avatarArt.js';
import { drawFurni } from '../game/furniArt.js';
import { closeWindow, useGame } from '../state/store.js';
import type { WindowId } from '../state/store.js';
import { iconUrl } from './icons.js';
import type { IconName } from './icons.js';

/* -------------------------------------------------------------------------- */
/* Pixel images                                                               */
/* -------------------------------------------------------------------------- */

export function PixelIcon({ name, size = 24, className }: { name: IconName; size?: number; className?: string }) {
  return (
    <img
      className={`pixel-icon ${className ?? ''}`}
      src={iconUrl(name)}
      width={size}
      height={size}
      alt=""
      draggable={false}
    />
  );
}

const spriteCache = new Map<string, { url: string; width: number; height: number }>();

function avatarImage(look: Look, direction: Direction, action: AvatarAction, frame: number) {
  const pose = { direction, action, frame, carrying: null, sipping: false, phone: false, blinking: false } as const;
  const key = `full.${avatarKey(look, pose)}`;
  let hit = spriteCache.get(key);
  if (!hit) {
    const pixels = drawAvatar(look, pose).trimmed(1);
    hit = { url: pixels.toCanvas().toDataURL(), width: pixels.width, height: pixels.height };
    spriteCache.set(key, hit);
  }
  return hit;
}

/** A full-body avatar, optionally animated, for menus and the lobby. */
export function AvatarSprite({
  look,
  direction = 3,
  action = 'stand',
  scale = 2,
  animate = false,
}: {
  look: Look;
  direction?: Direction;
  action?: AvatarAction;
  scale?: number;
  animate?: boolean;
}) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    if (!animate) return;
    const interval = window.setInterval(() => setFrame((f) => (f + 1) % 4), action === 'walk' ? 110 : 220);
    return () => window.clearInterval(interval);
  }, [animate, action]);

  const image = avatarImage(look, direction, action, animate ? frame : 0);
  return (
    <img
      className="pixel-img"
      src={image.url}
      width={image.width * scale}
      height={image.height * scale}
      alt=""
      draggable={false}
    />
  );
}

/** Head-only crop for lists and chat. */
export function AvatarHead({ look, scale = 1 }: { look: Look; scale?: number }) {
  const image = useMemo(() => {
    const pose = { direction: 3, action: 'stand', frame: 0, carrying: null, sipping: false, phone: false, blinking: false } as const;
    const key = `head.${avatarKey(look, pose)}`;
    let hit = spriteCache.get(key);
    if (!hit) {
      const full = drawAvatar(look, pose);
      // Head and shoulders: the top of the sprite, cropped.
      const head = full.trimmed(0);
      const crop = document.createElement('canvas');
      crop.width = head.bufWidth;
      crop.height = 30 * head.scale;
      crop.getContext('2d')?.drawImage(head.toCanvas(), 0, 0);
      hit = { url: crop.toDataURL(), width: head.width, height: 30 };
      spriteCache.set(key, hit);
    }
    return hit;
  }, [look]);
  return (
    <img
      className="pixel-img avatar-head"
      src={image.url}
      width={image.width * scale}
      height={image.height * scale}
      alt=""
      draggable={false}
    />
  );
}

export function FurniThumb({ kind, rotation = 2, scale = 1 }: { kind: string; rotation?: number; scale?: number }) {
  const key = `furni.${kind}.${rotation}`;
  let hit = spriteCache.get(key);
  if (!hit) {
    const pixels = drawFurni(kind, rotation).trimmed(1);
    hit = { url: pixels.toCanvas().toDataURL(), width: pixels.width, height: pixels.height };
    spriteCache.set(key, hit);
  }
  return (
    <img
      className="pixel-img"
      src={hit.url}
      width={hit.width * scale}
      height={hit.height * scale}
      alt=""
      draggable={false}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Windows                                                                    */
/* -------------------------------------------------------------------------- */

let topZ = 20;

/** Where each window first appears, as a fraction of the viewport. */
const ANCHORS: Partial<Record<WindowId, [number, number]>> = {
  navigator: [0.06, 0.1],
  shop: [0.3, 0.08],
  inventory: [0.55, 0.14],
  wardrobe: [0.1, 0.12],
  leaderboard: [0.62, 0.1],
  help: [0.35, 0.12],
  account: [0.4, 0.16],
  drinks: [0.4, 0.3],
  chatlog: [0.02, 0.35],
};

function initialPosition(id: WindowId, width: number): { x: number; y: number } {
  const [fx, fy] = ANCHORS[id] ?? [0.3, 0.15];
  const x = Math.round(Math.min(window.innerWidth - width - 12, Math.max(12, window.innerWidth * fx)));
  const y = Math.round(Math.max(56, window.innerHeight * fy));
  return { x, y };
}

export function Window({
  id,
  title,
  icon,
  width = 380,
  tall = false,
  resizable = false,
  children,
  footer,
}: {
  id: WindowId;
  title: string;
  icon?: IconName;
  width?: number;
  /** Give the body more room before it scrolls, for content-heavy boards like mahjong. */
  tall?: boolean;
  /** Desktop only: let the player drag a corner handle to stretch the window. */
  resizable?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const open = useGame((s) => Boolean(s.windows[id]));
  const [pos, setPos] = useState(() => initialPosition(id, width));
  const [z, setZ] = useState(() => ++topZ);
  const [sizeOverride, setSizeOverride] = useState<{ width: number; height: number } | null>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const resizeDrag = useRef<{ startX: number; startY: number; startWidth: number; startHeight: number } | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const effectiveWidth = sizeOverride?.width ?? width;

  // Raise a window whenever it is (re)opened.
  useEffect(() => {
    if (open) setZ(++topZ);
  }, [open]);

  if (!open) return null;

  const onHeaderDown = (event: ReactPointerEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest('button')) return;
    drag.current = { dx: event.clientX - pos.x, dy: event.clientY - pos.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onHeaderMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (!drag.current) return;
    setPos({
      x: Math.min(window.innerWidth - 60, Math.max(-effectiveWidth + 80, event.clientX - drag.current.dx)),
      y: Math.min(window.innerHeight - 40, Math.max(0, event.clientY - drag.current.dy)),
    });
  };
  const onHeaderUp = () => {
    drag.current = null;
  };

  const onResizeDown = (event: ReactPointerEvent<HTMLElement>) => {
    event.stopPropagation();
    resizeDrag.current = {
      startX: event.clientX,
      startY: event.clientY,
      startWidth: effectiveWidth,
      startHeight: sizeOverride?.height ?? bodyRef.current?.getBoundingClientRect().height ?? 300,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onResizeMove = (event: ReactPointerEvent<HTMLElement>) => {
    const d = resizeDrag.current;
    if (!d) return;
    const maxWidth = window.innerWidth - pos.x - 12;
    const maxBodyHeight = window.innerHeight - pos.y - 96;
    setSizeOverride({
      width: Math.round(Math.min(maxWidth, Math.max(320, d.startWidth + (event.clientX - d.startX)))),
      height: Math.round(Math.min(maxBodyHeight, Math.max(200, d.startHeight + (event.clientY - d.startY)))),
    });
  };
  const onResizeUp = () => {
    resizeDrag.current = null;
  };

  const style: CSSProperties = { left: pos.x, top: pos.y, width: effectiveWidth, zIndex: z };
  return (
    <section
      className={`window ${tall ? 'window-tall' : ''}`}
      style={style}
      role="dialog"
      aria-label={title}
      onPointerDown={() => setZ(++topZ)}
    >
      <header
        className="window-title"
        onPointerDown={onHeaderDown}
        onPointerMove={onHeaderMove}
        onPointerUp={onHeaderUp}
        onPointerCancel={onHeaderUp}
      >
        {icon && <PixelIcon name={icon} size={24} />}
        <h2>{title}</h2>
        <button className="window-close" onClick={() => closeWindow(id)} aria-label={`Close ${title}`}>
          <PixelIcon name="close" size={18} />
        </button>
      </header>
      <div
        className="window-body"
        ref={bodyRef}
        style={sizeOverride ? { maxHeight: sizeOverride.height } : undefined}
      >
        {children}
      </div>
      {footer && <footer className="window-footer">{footer}</footer>}
      {resizable && (
        <div
          className="window-resize"
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onPointerCancel={onResizeUp}
          aria-hidden
        />
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Small bits                                                                 */
/* -------------------------------------------------------------------------- */

/** An amount of Kaki Dollars with the coin icon, e.g. "K$1,500". */
export function Money({ value, size = 18 }: { value: number; size?: number }) {
  return (
    <span className="money">
      <PixelIcon name="coin" size={size} />
      {formatMoney(value)}
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="spinner" role="status">
      <span className="spinner-cup" aria-hidden />
      {label}...
    </div>
  );
}
